import { Component, Input, forwardRef } from '@angular/core';
import { EntityComponent } from '../share/entity-component';
import {
  Mesh,
  PlaneGeometry,
  MeshBasicMaterial,
  DoubleSide,
  DataTexture,
  RGBFormat,
  Color,
  Vector2,
  Vector3,
} from 'three';
import { Plane2Model, PlaneTextureData, EntityModel } from '../../model';
import { SceneService } from '../scene.service';
import { EntityService } from '../entity.service';
import { BoundingBox2FromCenterSize } from '../../share/boundingbox2';
import { BehaviorSubject } from 'rxjs';
import { provideParent } from '../share/provideParent';
import { RendererService } from '../renderer.service';

@Component({
  selector: 'ng3-plane',
  templateUrl: './plane.component.html',
  styles: ['./plane.component.css'],
  providers: [provideParent(PlaneComponent)],
  // providers: [{ provide: EntityComponent, useExisting: forwardRef(() => PlaneComponent) }],
})
export class PlaneComponent extends EntityComponent<Plane2Model> {
  // @Input() z = -100;
  private zSubject = new BehaviorSubject<number>(-100);
  @Input()
  get z(): number {
    return this.zSubject.value;
  }
  set z(n: number) {
    this.zSubject.next(n);
  }

  constructor(sceneService: SceneService, renderer: RendererService) {
    super(sceneService, renderer);
  }

  createTexture(data: PlaneTextureData) {
    const textureData = new Uint8Array(3 * data.shape.x * data.shape.y);
    const texture = new DataTexture(
      textureData,
      data.shape.x,
      data.shape.y,
      RGBFormat,
    );
    for (let i = 0; i < data.shape.x; i++) {
      for (let j = 0; j < data.shape.y; j++) {
        const indImg = i * data.shape.y + j;
        const indTex = i + j * data.shape.x;
        const stride = indTex * 3;
        textureData[stride] = data.r[indImg];
        textureData[stride + 1] = data.g[indImg];
        textureData[stride + 2] = data.b[indImg];
      }
    }

    texture.needsUpdate = true;
    return texture;
  }

  createEntity(model: Plane2Model) {
    // console.log('Create Entity based on model', model);
    const map =
      model.textureData === undefined
        ? undefined
        : this.createTexture(model.textureData);
    const color =
      map !== undefined
        ? undefined
        : model.color !== undefined
        ? model.color
        : new Color(0.5, 0.5, 0.5);
    const base = {
      side: DoubleSide,
      wireframe: model.wireFrame === undefined ? false : model.wireFrame,
      opacity: model.opacity === undefined ? 1.0 : model.opacity,
      transparent: model.transparent === undefined ? false : model.transparent,
    };
    const rotation =
      model.rotation === undefined ? new Vector3(0, 0, 0) : model.rotation;
    const result = new Mesh(
      new PlaneGeometry(1, 1, 1, 1),
      new MeshBasicMaterial(
        map !== undefined ? { ...base, map } : { ...base, color },
      ),
    );
    result.position.set(model.box.center.x, model.box.center.y, this.z);
    result.scale.set(model.box.size.x, model.box.size.y, 1.0);
    result.rotation.set(rotation.x, rotation.y, rotation.z);
    result.renderOrder = this.z;
    return result;
  }

  updateEntity(model: Plane2Model, entity: Mesh) {
    if (model.textureData !== undefined) {
      this.changeTexture(entity as Mesh, model.textureData);
    }
    (entity.material as MeshBasicMaterial).setValues({
      wireframe: model.wireFrame,
      transparent: model.transparent,
      opacity: model.opacity,
    });
    (entity.material as MeshBasicMaterial).needsUpdate = true;

    entity.position.set(model.box.center.x, model.box.center.y, this.z);
    entity.scale.set(model.box.size.x, model.box.size.y, 1.0);
    entity.rotation.set(model.rotation.x, model.rotation.y, model.rotation.z);
    entity.renderOrder = this.z;
    return entity;
  }

  private changeTexture(plane: Mesh, data: PlaneTextureData): void {
    (plane.material as MeshBasicMaterial).setValues({
      map: this.createTexture(data),
    });
  }

  defaultModel() {
    return new Plane2Model(
      new BoundingBox2FromCenterSize(new Vector2(0, 0), new Vector2(100, 100)),
      10,
      {
        r: new Uint8Array([255]),
        g: new Uint8Array([255]),
        b: new Uint8Array([255]),
        shape: new Vector2(1, 1),
      },
    );
  }
}
